import { AnimatePresence, motion } from "framer-motion";
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Download,
  Eye,
  File,
  FileSpreadsheet,
  FileText,
  Image,
  MessageSquareWarning,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import API from "../../lib/api";
import OfficePreview, { isOfficePreviewable } from "../OfficePreview";
import ConfirmModal from "./ConfirmModal";
import DeliverablesUploadPanel from "./DeliverablesUploadPanel";
import DraftRevisionsPanel from "./DraftRevisionsPanel";
import MeetingBookingPanel from "./MeetingBookingPanel";
import StepStatusPanel from "./StepStatusPanel";

// Steps that get a client-visible "Status Update" composer. Generic so we
// can add more later — Filed ITR is the canonical one.
const STATUS_UPDATE_STEP_NAMES = new Set(["Filed ITR"]);
const hasStatusUpdate = (n) => STATUS_UPDATE_STEP_NAMES.has(n);

// Steps that follow the versioned upload → review → approve loop instead of
// the default "CA clicks Complete Step" flow. Matches the backend allowlist
// in draftRevisionService.js.
const REVIEW_STEP_NAMES = new Set([
  "Draft ITR",
  "Drafting",
  "Proposal",
  "Strategy",
  "Opinion Draft",
  "LLP Agreement",
  "Application Filing",
]);
const isReviewStepName = (n) => REVIEW_STEP_NAMES.has(n);

// Steps that use the live meeting booking flow. Matches meetingService.js.
const MEETING_STEP_NAMES = new Set(["Virtual Meeting", "Consultation"]);
const isMeetingStepName = (n) => MEETING_STEP_NAMES.has(n);

// ── Authenticated download / preview helpers ──────────────────────────────
async function fetchDocBlob(doc, { inline = false } = {}) {
  // doc.download_url starts with "/api/" but the axios baseURL already
  // ends in "/api" — strip the prefix to avoid "/api/api/..." URLs.
  const path = (doc.download_url || "").replace(/^\/api/, "");
  const url = `${path}${inline ? "?inline=1" : ""}`;
  const { data } = await API.get(url, { responseType: "blob" });
  return URL.createObjectURL(data);
}
function previewKind(mime, name) {
  if (mime?.startsWith("image/")) return "image";
  if (mime === "application/pdf") return "pdf";
  if (isOfficePreviewable({ mime, name })) return "office";
  return null;
}
const isPreviewableMime = (m, n) => previewKind(m, n) !== null;

function DocActions({ doc }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [previewUrl, setUrl] = useState(null);

  const errFor = (err) =>
    err.response?.status === 403
      ? "Access denied."
      : err.response?.status === 404
        ? "File not found."
        : "Failed.";

  const onDownload = async () => {
    setBusy(true);
    setError("");
    try {
      const u = await fetchDocBlob(doc);
      const a = document.createElement("a");
      a.href = u;
      a.download = doc.original_name || `document-${doc.id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(u), 1000);
    } catch (err) {
      setError(errFor(err));
    }
    setBusy(false);
  };

  const onPreview = async () => {
    setBusy(true);
    setError("");
    try {
      setUrl(await fetchDocBlob(doc, { inline: true }));
    } catch (err) {
      setError(errFor(err));
    }
    setBusy(false);
  };

  return (
    <>
      <div className="flex items-center gap-3 whitespace-nowrap">
        {isPreviewableMime(doc.mime_type, doc.original_name) && (
          <button
            onClick={onPreview}
            disabled={busy}
            className="flex items-center gap-1 text-[#6C63FF] hover:underline disabled:opacity-50"
          >
            <Eye className="h-3 w-3" /> Preview
          </button>
        )}
        <button
          onClick={onDownload}
          disabled={busy}
          className="flex items-center gap-1 text-[#6C63FF] hover:underline disabled:opacity-50"
        >
          <Download className="h-3 w-3" /> {busy ? "…" : "Download"}
        </button>
      </div>
      {error && (
        <p className="text-[10px] text-red-500 font-body mt-0.5">{error}</p>
      )}
      {previewUrl && (
        <DocPreview
          doc={doc}
          url={previewUrl}
          onClose={() => {
            URL.revokeObjectURL(previewUrl);
            setUrl(null);
          }}
        />
      )}
    </>
  );
}

function DocPreview({ doc, url, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const kind = previewKind(doc.mime_type, doc.original_name);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 py-6"
      onClick={onClose}
    >
      <div
        className="w-full max-w-5xl h-full max-h-[90vh] bg-white rounded-2xl shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E8E6F8]">
          <p className="text-sm font-heading font-bold text-[#1A1A2E] truncate">
            {doc.original_name}
          </p>
          <button
            onClick={onClose}
            className="text-[#9CA3AF] hover:text-[#1A1A2E]"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-auto bg-[#F5F4FB] flex items-center justify-center">
          {kind === "image" ? (
            <img
              src={url}
              alt={doc.original_name}
              className="max-w-full max-h-full object-contain"
            />
          ) : kind === "pdf" ? (
            <iframe
              src={url}
              title={doc.original_name}
              className="w-full h-full"
            />
          ) : kind === "office" ? (
            <div className="w-full h-full overflow-auto">
              <OfficePreview
                url={url}
                mime={doc.mime_type}
                name={doc.original_name}
              />
            </div>
          ) : (
            <div className="text-center px-6 py-12">
              <File className="h-14 w-14 text-[#9CA3AF] mx-auto mb-3" />
              <p className="text-sm font-heading font-semibold text-[#1A1A2E]">
                Preview not available in browser
              </p>
              <p className="text-xs text-[#6B7280] font-body mt-1 max-w-md mx-auto">
                This file type can't be rendered inline. Download to open in the
                matching desktop application.
              </p>
              <a
                href={url}
                download={doc.original_name}
                className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-lg bg-[#6C63FF] hover:bg-[#5B52CC] text-white text-xs font-body"
              >
                <Download className="h-3.5 w-3.5" /> Download
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Document type labels ──────────────────────────────────────────────────
const DOC_LABELS = {
  aadhar_card: "Aadhar Card",
  pan_card: "PAN Card",
  income_tax_password: "Income Tax Portal",
  bank_statements: "Bank Statements",
  form_16: "Form 16",
  form_26as_ais: "Form 26AS / AIS",
  life_insurance: "Life Insurance Premium",
  health_insurance: "Health Insurance Premium",
  capital_gains: "Capital Gains Statement",
  rent_receipts: "Rent Receipts",
  home_loan_interest: "Home Loan Interest Certificate",
  section_80c_80d: "80C / 80D Investment Proofs",
  donation_80g: "Donation Receipts (80G)",
  business_freelance: "Business/Freelance Income",
  foreign_income: "Foreign Income/Assets",
};

function docLabel(type) {
  if (!type) return "Document";
  if (type.startsWith("other_")) return "Other Document";
  if (type.startsWith("admin_"))
    return type.replace("admin_", "").replace(/_/g, " ");
  if (type.startsWith("unknown_")) return "Unknown Document";
  return (
    DOC_LABELS[type] ||
    type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

// ISO-8601 date-time detector (e.g. "2026-05-21T14:54:10.818Z").
const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/;

// Render step_data scalar values. ISO date-times are converted to IST with
// seconds (e.g. "21 May 2026, 8:24:10 pm IST"); everything else is shown as-is.
function formatStepValue(v) {
  if (typeof v === "string" && ISO_DATETIME_RE.test(v)) {
    const d = new Date(v);
    if (!isNaN(d.getTime())) {
      const s = d.toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
      return `${s} IST`;
    }
  }
  return String(v);
}

function formatSize(bytes) {
  if (!bytes) return "–";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function mimeIcon(mime) {
  if (!mime) return File;
  if (mime.includes("pdf")) return FileText;
  if (mime.includes("image")) return Image;
  if (mime.includes("sheet") || mime.includes("excel")) return FileSpreadsheet;
  return File;
}
function mimeColor(mime) {
  if (!mime) return "#9CA3AF";
  if (mime.includes("pdf")) return "#E57373";
  if (mime.includes("image")) return "#4FC3F7";
  if (mime.includes("sheet") || mime.includes("excel")) return "#81C784";
  return "#9CA3AF";
}

// ── Status config ─────────────────────────────────────────────────────────
const STATUS_CFG = {
  completed: {
    label: "Completed",
    badgeClass: "bg-green-100  text-green-700",
    cardClass: "bg-green-50  border-green-200",
  },
  admin_pending: {
    label: "⚡ CA Action Required",
    badgeClass: "bg-orange-100 text-orange-700",
    cardClass: "bg-orange-50 border-orange-300",
  },
  pending: {
    label: "Waiting for Client",
    badgeClass: "bg-blue-100   text-blue-700",
    cardClass: "bg-blue-50   border-blue-200",
  },
  upcoming: {
    label: "Not Started",
    badgeClass: "bg-gray-100   text-gray-500",
    cardClass: "bg-gray-50   border-gray-200",
  },
};

// ── Sub-section: Documents (Step 1) ──────────────────────────────────────
function StepDocuments({ docs, stepData }) {
  const panNumber = stepData?.pan_number;

  return (
    <div className="space-y-3">
      {panNumber && (
        <div className="flex items-center gap-2 px-3 py-2 bg-[#6C63FF]/5 rounded-lg border border-[#6C63FF]/20">
          <span className="text-xs font-body text-[#6B7280]">PAN Number:</span>
          <span className="text-sm font-heading font-bold text-[#6C63FF] tracking-widest">
            {panNumber}
          </span>
        </div>
      )}

      {docs.length === 0 ? (
        <p className="text-xs text-[#9CA3AF] font-body">
          No documents found for this step
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[#E8E6F8]">
          <table className="w-full text-xs font-body">
            <thead>
              <tr className="bg-[#F5F4FB] border-b border-[#E8E6F8]">
                {["Document Type", "File Name", "Size", "Uploaded At", ""].map(
                  (h) => (
                    <th
                      key={h}
                      className="px-3 py-2.5 text-left font-heading font-semibold text-[#6B7280] whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E8E6F8]">
              {docs.map((doc) => {
                const Icon = mimeIcon(doc.mime_type);
                const color = mimeColor(doc.mime_type);
                return (
                  <tr key={doc.id} className="bg-white hover:bg-[#F5F4FB]">
                    <td className="px-3 py-2.5 font-medium text-[#1A1A2E] whitespace-nowrap">
                      {docLabel(doc.document_type)}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <Icon
                          className="h-3.5 w-3.5 flex-shrink-0"
                          style={{ color }}
                        />
                        <span className="text-[#4A4A60] max-w-[200px] truncate">
                          {doc.original_name}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-[#9CA3AF] whitespace-nowrap">
                      {formatSize(doc.file_size)}
                    </td>
                    <td className="px-3 py-2.5 text-[#9CA3AF] whitespace-nowrap">
                      {doc.uploaded_at
                        ? new Date(doc.uploaded_at).toLocaleString("en-IN")
                        : "–"}
                    </td>
                    <td className="px-3 py-2.5">
                      <DocActions doc={doc} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Sub-section: Payment (Step 2) ─────────────────────────────────────────
function StepPayment({ payments }) {
  if (!payments || payments.length === 0) {
    return (
      <p className="text-xs text-[#9CA3AF] font-body">
        No payment records found
      </p>
    );
  }
  const latest = payments[payments.length - 1];
  const statusClass =
    {
      completed: "bg-green-100 text-green-700",
      pending: "bg-yellow-100 text-yellow-700",
      failed: "bg-red-100 text-red-700",
    }[latest.status] || "bg-gray-100 text-gray-500";

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      {[
        {
          label: "Amount",
          value: `₹${Number(latest.amount || 0).toLocaleString("en-IN")}`,
          large: true,
        },
        { label: "Payment ID", value: latest.payment_id || "–", mono: true },
        {
          label: "Paid At",
          value: latest.paid_at
            ? new Date(latest.paid_at).toLocaleString("en-IN")
            : "–",
        },
        { label: "Currency", value: latest.currency || "INR" },
        { label: "Method", value: latest.payment_method || "Online" },
      ].map(({ label, value, large, mono }) => (
        <div
          key={label}
          className="bg-white rounded-xl border border-[#E8E6F8] p-3"
        >
          <p className="text-[10px] font-body text-[#9CA3AF] uppercase tracking-wide mb-1">
            {label}
          </p>
          <p
            className={`font-body text-[#1A1A2E] ${large ? "text-lg font-heading font-bold text-[#6C63FF]" : mono ? "text-xs font-mono" : "text-sm"}`}
          >
            {value}
          </p>
        </div>
      ))}
      <div className="bg-white rounded-xl border border-[#E8E6F8] p-3">
        <p className="text-[10px] font-body text-[#9CA3AF] uppercase tracking-wide mb-1">
          Status
        </p>
        <span
          className={`text-xs font-body font-medium px-2 py-0.5 rounded-full ${statusClass}`}
        >
          {latest.status}
        </span>
      </div>
    </div>
  );
}

// ── Sub-section: Computation inline ──────────────────────────────────────
function StepComputation({ order, orderId, onRefresh }) {
  const [pct, setPct] = useState(order?.computation_percentage ?? 0);
  const [msg, setMsg] = useState(order?.computation_message ?? "");
  const [saving, setSaving] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const origPct = order?.computation_percentage ?? 0;
  const origMsg = order?.computation_message ?? "";
  const unchanged = pct === origPct && msg === origMsg;

  const handleSend = async () => {
    setError("");
    setSaving(true);
    try {
      await API.put(`/admin/orders/${orderId}/computation`, {
        percentage: pct,
        message: msg,
      });
      setSent(true);
      setTimeout(() => setSent(false), 2500);
      if (onRefresh) await onRefresh();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update computation");
    }
    setSaving(false);
  };

  const circumference = 2 * Math.PI * 38;
  const offset = circumference * (1 - pct / 100);

  return (
    <div className="space-y-4">
      {/* Current state */}
      <div className="bg-white rounded-xl border border-[#E8E6F8] p-4">
        <p className="text-xs font-heading font-semibold text-[#6B7280] mb-3 uppercase tracking-wide">
          What Client Currently Sees
        </p>
        {order?.computation_percentage == null ? (
          <p className="text-sm text-[#9CA3AF] font-body italic">
            Not yet updated — client sees "Under Review"
          </p>
        ) : (
          <div className="flex items-center gap-4">
            <div className="relative w-16 h-16 flex-shrink-0">
              <svg className="w-full h-full -rotate-90" viewBox="0 0 84 84">
                <circle
                  cx="42"
                  cy="42"
                  r="38"
                  fill="none"
                  stroke="#E8E6F8"
                  strokeWidth="6"
                />
                <circle
                  cx="42"
                  cy="42"
                  r="38"
                  fill="none"
                  stroke="#6C63FF"
                  strokeWidth="6"
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={circumference * (1 - origPct / 100)}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-sm font-heading font-bold text-[#6C63FF]">
                  {origPct}%
                </span>
              </div>
            </div>
            <div>
              <p className="text-sm font-body text-[#1A1A2E]">
                {order.computation_message || "No message set"}
              </p>
              {order.computation_updated_at && (
                <p className="text-xs text-[#9CA3AF] font-body mt-1">
                  Updated{" "}
                  {new Date(order.computation_updated_at).toLocaleString(
                    "en-IN",
                  )}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Update form */}
      <div className="bg-white rounded-xl border border-[#E8E6F8] p-4 space-y-4">
        <p className="text-xs font-heading font-semibold text-[#1A1A2E] uppercase tracking-wide">
          Send Update to Client
        </p>

        {/* Percentage slider */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-body text-[#6B7280]">
              Computation Progress
            </label>
            <span className="text-sm font-heading font-bold text-[#6C63FF]">
              {pct}%
            </span>
          </div>

          {/* Preview circle */}
          <div className="flex items-center gap-4 mb-3">
            <div className="relative w-14 h-14 flex-shrink-0">
              <svg className="w-full h-full -rotate-90" viewBox="0 0 84 84">
                <circle
                  cx="42"
                  cy="42"
                  r="38"
                  fill="none"
                  stroke="#E8E6F8"
                  strokeWidth="7"
                />
                <circle
                  cx="42"
                  cy="42"
                  r="38"
                  fill="none"
                  stroke="#66BB6A"
                  strokeWidth="7"
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={offset}
                  style={{ transition: "stroke-dashoffset 0.4s ease" }}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xs font-heading font-bold text-[#6C63FF]">
                  {pct}%
                </span>
              </div>
            </div>
            <div className="flex-1 space-y-2">
              <input
                type="range"
                min={0}
                max={100}
                value={pct}
                onChange={(e) => setPct(Number(e.target.value))}
                className="w-full accent-[#6C63FF]"
              />
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={pct}
                  onChange={(e) =>
                    setPct(Math.max(0, Math.min(100, Number(e.target.value))))
                  }
                  className="w-16 text-sm font-body text-center border border-[#E8E6F8] rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
                />
                <span className="text-xs text-[#9CA3AF] font-body">
                  Client will see:{" "}
                  <strong className="text-[#6C63FF]">{pct}%</strong>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Message */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-body text-[#6B7280]">
              Message to Client
            </label>
            <span className="text-[10px] text-[#9CA3AF] font-body">
              {msg.length}/500
            </span>
          </div>
          <textarea
            rows={3}
            maxLength={500}
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            placeholder="e.g. We have reviewed your documents. Your tax computation is in progress and will be ready within 24 hours..."
            className="w-full text-sm font-body border border-[#E8E6F8] rounded-lg p-2.5 resize-none focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
          />
        </div>

        {error && <p className="text-xs text-red-500 font-body">{error}</p>}

        <button
          onClick={handleSend}
          disabled={saving || unchanged}
          className={`w-full py-2.5 text-sm font-body font-medium rounded-xl transition-all ${
            sent
              ? "bg-green-500 text-white"
              : unchanged
                ? "bg-[#E8E6F8] text-[#9CA3AF] cursor-not-allowed"
                : "bg-[#6C63FF] hover:bg-[#5B52CC] text-white shadow-sm"
          }`}
        >
          {saving
            ? "Sending…"
            : sent
              ? "✓ Sent to Client"
              : "Send Update to Client"}
        </button>
      </div>
    </div>
  );
}

// ── Calendly meeting booking history ──────────────────────────────────────
// Calendly is external, so we don't receive the actual chosen slot time — what
// we record is each time the client confirmed a booking (and any reschedules).
// Show the latest as the active booking and older ones as rescheduled history.
function CalendlyBookingHistory({ bookings, confirmedAt }) {
  const fmt = (v) => {
    if (!v) return null;
    const d = new Date(v);
    return isNaN(d.getTime())
      ? null
      : `${d.toLocaleString("en-IN", {
          timeZone: "Asia/Kolkata",
          day: "numeric",
          month: "short",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        })} IST`;
  };

  // Prefer the booking trail from scheduled_calls; fall back to the single
  // confirmation timestamp stored on the step when no rows exist.
  const rows =
    Array.isArray(bookings) && bookings.length > 0
      ? bookings
      : confirmedAt
        ? [{ id: "step", status: "pending", created_at: confirmedAt }]
        : [];

  if (rows.length === 0) return null;

  const isRescheduled = (b) =>
    b.status === "cancelled" && /^Rescheduled/i.test(b.response_notes || "");
  const isActive = (b) => b.status === "pending" || b.status === "confirmed";

  return (
    <div className="bg-white rounded-xl border border-[#E8E6F8] p-3">
      <p className="text-xs font-heading font-semibold text-[#6B7280] mb-2 uppercase tracking-wide">
        Calendly Bookings ({rows.length})
      </p>
      <ul className="space-y-2">
        {rows.map((b, i) => {
          const active = isActive(b);
          const resched = isRescheduled(b);
          // The first active row in a newest-first list is the live booking.
          const isLatestActive =
            active && rows.findIndex((r) => isActive(r)) === i;
          return (
            <li
              key={b.id}
              className={`flex items-start gap-2 rounded-lg px-3 py-2 text-xs font-body ${
                isLatestActive
                  ? "bg-green-50 border border-green-200"
                  : "bg-[#F5F4FB] border border-[#E8E6F8]"
              }`}
            >
              <span
                className={`mt-0.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                  isLatestActive ? "bg-green-500" : "bg-gray-400"
                }`}
              />
              <div className="min-w-0">
                <p className="text-[#1A1A2E] font-medium">
                  {isLatestActive
                    ? "Active booking"
                    : resched
                      ? "Rescheduled (replaced)"
                      : active
                        ? "Booking"
                        : "Earlier booking"}
                </p>
                <p className="text-[#6B7280] mt-0.5">
                  Client confirmed on{" "}
                  <span className="font-medium text-[#4A4A60]">
                    {fmt(b.created_at) || "—"}
                  </span>
                </p>
                {b.response_notes && (
                  <p className="text-[#9CA3AF] italic mt-0.5">
                    {b.response_notes}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      <p className="text-[10px] text-[#9CA3AF] font-body mt-2">
        Times shown are when the client confirmed each booking. The exact slot
        lives in your Calendly calendar / email invite.
      </p>
    </div>
  );
}

// ── Main WorkflowStepCard ─────────────────────────────────────────────────
export default function WorkflowStepCard({
  step,
  orderId,
  serviceId,
  order,
  onRefresh,
}) {
  const cfg = STATUS_CFG[step.status] || STATUS_CFG.upcoming;
  const isAdminPending = step.status === "admin_pending";
  const isCompleted = step.status === "completed";
  const isComputation = step.name === "Computation";
  const isDocuments = step.name === "Documents";
  const isPayment = step.name === "Payment";
  const isReviewStep = isReviewStepName(step.name);
  const isMeetingStep = isMeetingStepName(step.name);
  // Calendly path: the client booked on the CA's Calendly and confirmed, which
  // parks the step in admin_pending with calendly_booked = true. There's no
  // live booking row, so the live MeetingBookingPanel doesn't apply — the CA
  // simply marks the step complete after the meeting happens.
  const isCalendlyBooked =
    isMeetingStep && step.step_data?.calendly_booked === true;
  // Client opted out of the meeting — step completed with a mandatory reason.
  const isMeetingSkipped =
    isMeetingStep && step.step_data?.meeting_skipped === true;
  const isDeliverables = step.name === "Deliverables";
  const isStatusStep = hasStatusUpdate(step.name);

  // Auto-expand for action-needed or data-rich steps. Deliverables is the
  // CA's chance to upload final files even while the step is "pending" on
  // the client side, so always start it open.
  const [open, setOpen] = useState(
    isAdminPending ||
      isDeliverables ||
      (isCompleted && (isDocuments || isPayment)),
  );

  const [completeOpen, setComplete] = useState(false);
  const [reopenOpen, setReopen] = useState(false);
  const [notes, setNotes] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  // Documents for this step
  const stepDocs = (order?.documents || []).filter(
    (d) =>
      d.step_number === step.step_number &&
      !d.document_type?.startsWith("admin_"),
  );

  const handleComplete = async () => {
    setLoading(true);
    try {
      await API.put(
        `/admin/orders/${orderId}/steps/${step.step_number}/complete`,
        {
          notes: notes.trim() || undefined,
        },
      );
      setNotes("");
      setComplete(false);
      if (onRefresh) await onRefresh();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to complete step");
    }
    setLoading(false);
  };

  const handleReopen = async () => {
    setLoading(true);
    try {
      await API.put(
        `/admin/orders/${orderId}/steps/${step.step_number}/reopen`,
        {
          reason: reason.trim(),
        },
      );
      setReason("");
      setReopen(false);
      if (onRefresh) await onRefresh();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to reopen step");
    }
    setLoading(false);
  };

  const canExpand =
    isAdminPending ||
    isCompleted ||
    isDeliverables ||
    (step.step_data && Object.keys(step.step_data).length > 0);

  return (
    <motion.div
      layout
      className={`rounded-2xl border-2 transition-all duration-300 overflow-hidden ${cfg.cardClass} ${
        isAdminPending ? "ring-2 ring-orange-300 ring-offset-1" : ""
      }`}
    >
      {/* Pulse bar for admin_pending */}
      {isAdminPending && (
        <div className="h-1 w-full bg-gradient-to-r from-orange-400 via-yellow-400 to-orange-400 animate-pulse" />
      )}

      {/* Header */}
      <div
        className="flex items-center gap-3 p-4 cursor-pointer select-none"
        onClick={() => canExpand && setOpen(!open)}
      >
        {/* Step number badge */}
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold font-heading flex-shrink-0 ${
            isCompleted
              ? "bg-green-500 text-white"
              : isAdminPending
                ? "bg-orange-500 text-white"
                : "bg-white border-2 border-current text-[#6B7280]"
          }`}
        >
          {isCompleted ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : (
            step.step_number
          )}
        </div>

        {/* Step name + badge */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-body font-semibold text-[#1A1A2E]">
              {step.name}
            </span>
            <span
              className={`text-[10px] font-body font-semibold px-2 py-0.5 rounded-full ${cfg.badgeClass}`}
            >
              {cfg.label}
            </span>
          </div>
          {isCompleted && step.completed_at && (
            <p className="text-[10px] text-[#9CA3AF] font-body mt-0.5">
              Completed {new Date(step.completed_at).toLocaleString("en-IN")}
            </p>
          )}
        </div>

        {/* Right actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {isAdminPending &&
            !isComputation &&
            !isReviewStep &&
            (!isMeetingStep || isCalendlyBooked) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setComplete(true);
                }}
                className="flex items-center gap-1.5 text-xs font-body font-medium px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                Complete Step
              </button>
            )}
          {isAdminPending && isReviewStep && (
            <span className="inline-flex items-center gap-1 text-[10px] font-body font-semibold text-[#6C63FF] bg-[#EEEDFC] px-2 py-0.5 rounded-full">
              Client must approve
            </span>
          )}
          {isAdminPending && isMeetingStep && !isCalendlyBooked && (
            <span className="inline-flex items-center gap-1 text-[10px] font-body font-semibold text-[#6C63FF] bg-[#EEEDFC] px-2 py-0.5 rounded-full">
              Meeting flow
            </span>
          )}
          {isCompleted && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setReopen(true);
              }}
              className="text-[10px] font-body px-2 py-1 bg-white hover:bg-orange-50 border border-orange-200 text-orange-600 rounded-lg transition-colors"
            >
              Reopen
            </button>
          )}
          {canExpand && (
            <div className="text-[#9CA3AF]">
              {open ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Expanded content */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-0 border-t border-white/30 space-y-4">
              {/* ── DOCUMENTS step ── */}
              {isDocuments && (
                <div>
                  <p className="text-xs font-heading font-semibold text-[#6B7280] mb-3 uppercase tracking-wide">
                    Uploaded Documents ({stepDocs.length})
                  </p>
                  <StepDocuments docs={stepDocs} stepData={step.step_data} />
                </div>
              )}

              {/* ── PAYMENT step ── */}
              {isPayment && (
                <div>
                  <p className="text-xs font-heading font-semibold text-[#6B7280] mb-3 uppercase tracking-wide flex items-center gap-1.5">
                    <CreditCard className="h-3.5 w-3.5" /> Payment Details
                  </p>
                  <StepPayment payments={order?.payments} />
                </div>
              )}

              {/* ── COMPUTATION step ── */}
              {isComputation && serviceId === "itr-filing" && (
                <div>
                  <p className="text-xs font-heading font-semibold text-[#6B7280] mb-3 uppercase tracking-wide">
                    Computation Management
                  </p>
                  <StepComputation
                    order={order}
                    orderId={orderId}
                    onRefresh={onRefresh}
                  />
                  {/* Mark complete button appears below update form */}
                  {isAdminPending && (
                    <div className="mt-4 pt-4 border-t border-orange-200">
                      <p className="text-xs font-body text-[#6B7280] mb-2">
                        When computation is complete and ready for client
                        review:
                      </p>
                      <button
                        onClick={() => setComplete(true)}
                        className="flex items-center gap-1.5 text-xs font-body font-medium px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        Mark Computation Complete → Advance to Draft ITR
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* ── OTHER steps: show step_data ── */}
              {!isDocuments &&
                !isPayment &&
                !isComputation &&
                step.step_data &&
                Object.keys(step.step_data).filter(
                  (k) =>
                    ![
                      "income_tax_password_enc",
                      "storage_path",
                      "files",
                      "meeting_skipped",
                      "skipped",
                      "skip_reason",
                    ].includes(k),
                ).length > 0 && (
                  <div>
                    <p className="text-xs font-heading font-semibold text-[#6B7280] mb-2 uppercase tracking-wide">
                      Step Details
                    </p>
                    <div className="bg-white/70 rounded-xl border border-white p-3 space-y-1">
                      {Object.entries(step.step_data)
                        .filter(
                          ([k]) =>
                            ![
                              "income_tax_password_enc",
                              "storage_path",
                              "files",
                              "meeting_skipped",
                              "skipped",
                              "skip_reason",
                            ].includes(k),
                        )
                        .map(([k, v]) => (
                          <div key={k} className="flex gap-2 text-xs font-body">
                            <span className="text-[#6C63FF] font-medium flex-shrink-0 capitalize">
                              {k.replace(/_/g, " ")}:
                            </span>
                            <span className="text-[#4A4A60] break-all">
                              {typeof v === "object"
                                ? JSON.stringify(v)
                                : formatStepValue(v)}
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

              {/* ── Review-step draft loop (Draft ITR etc.) ── */}
              {isReviewStep && (
                <DraftRevisionsPanel
                  orderId={orderId}
                  stepNumber={step.step_number}
                  stepName={step.name}
                  onRefresh={onRefresh}
                />
              )}

              {/* ── Client skipped the virtual meeting (with reason) ── */}
              {isMeetingStep && isMeetingSkipped && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <MessageSquareWarning className="h-4 w-4 text-amber-600" />
                    <p className="text-sm font-heading font-bold text-amber-800">
                      Client chose to skip the virtual meeting
                    </p>
                  </div>
                  <p className="text-[10px] uppercase tracking-wide font-heading font-semibold text-amber-700/80 mb-1">
                    Client's note
                  </p>
                  <p className="text-sm font-body text-[#1A1A2E] italic whitespace-pre-wrap break-words">
                    "{step.step_data?.skip_reason || "No note provided."}"
                  </p>
                </div>
              )}

              {/* ── Meeting booking flow (Virtual Meeting / Consultation) ── */}
              {isMeetingStep && !isCalendlyBooked && !isMeetingSkipped && (
                <MeetingBookingPanel
                  orderId={orderId}
                  stepNumber={step.step_number}
                  stepName={step.name}
                  onRefresh={onRefresh}
                />
              )}

              {/* ── Calendly-booked: client confirmed, CA marks complete ── */}
              {isMeetingStep && isCalendlyBooked && (
                <div className="space-y-3">
                  <div className="bg-orange-100/50 rounded-xl p-3 text-xs font-body text-orange-700">
                    The client booked this meeting on your Calendly and
                    confirmed. Once the meeting is done, click{" "}
                    <span className="font-semibold">"Complete Step"</span> to
                    advance them to the next step. They stay parked here until
                    you do.
                  </div>
                  <CalendlyBookingHistory
                    bookings={order?.meeting_bookings}
                    confirmedAt={step.step_data?.calendly_booked_at}
                  />
                </div>
              )}

              {/* ── Deliverables (CA uploads final files for the client) ── */}
              {isDeliverables && (
                <DeliverablesUploadPanel
                  orderId={orderId}
                  stepNumber={step.step_number}
                  existingDocs={(order?.documents || []).filter(
                    (d) => d.step_number === step.step_number,
                  )}
                  onRefresh={onRefresh}
                />
              )}

              {/* ── Status-update composer (Filed ITR etc.) ── */}
              {isStatusStep && (
                <StepStatusPanel
                  orderId={orderId}
                  stepNumber={step.step_number}
                  stepName={step.name}
                  initial={{
                    status_message: step.step_data?.status_message,
                    status_state: step.step_data?.status_state,
                    status_updated_at: step.step_data?.status_updated_at,
                    status_updated_by_name:
                      step.step_data?.status_updated_by_name,
                    status_history: step.step_data?.status_history,
                  }}
                  onRefresh={onRefresh}
                />
              )}

              {/* ── Plain admin_pending: generic action note ── */}
              {isAdminPending &&
                !isComputation &&
                !isReviewStep &&
                !isMeetingStep &&
                !isDeliverables &&
                !isStatusStep && (
                  <div className="bg-orange-100/50 rounded-xl p-3 text-xs font-body text-orange-700">
                    This step is waiting for CA team action. Click "Complete
                    Step" once the work is done.
                  </div>
                )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Complete modal */}
      <ConfirmModal
        open={completeOpen}
        onOpenChange={setComplete}
        title={`Complete: ${step.name}`}
        description={
          isComputation
            ? "Mark the Computation step as complete. The next step (Draft ITR) will be activated for the client."
            : "Mark this step as completed. The next step will be activated automatically."
        }
        confirmLabel={loading ? "Processing…" : "Mark Complete"}
        confirmVariant="primary"
        onConfirm={handleComplete}
      >
        <div className="mb-4">
          <label className="text-xs font-body text-[#6B7280] mb-1 block">
            Internal Notes (optional)
          </label>
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add internal notes for this completion…"
            className="w-full text-sm font-body border border-[#E8E6F8] rounded-lg p-2.5 resize-none focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
          />
        </div>
      </ConfirmModal>

      {/* Reopen modal */}
      <ConfirmModal
        open={reopenOpen}
        onOpenChange={setReopen}
        title={`Reopen: ${step.name}`}
        description="Set this step back to admin_pending. Provide a reason."
        confirmLabel="Reopen Step"
        confirmVariant="danger"
        onConfirm={handleReopen}
      >
        <div className="mb-4">
          <label className="text-xs font-body text-[#6B7280] mb-1 block">
            Reason *
          </label>
          <textarea
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is this step being reopened?"
            className="w-full text-sm font-body border border-[#E8E6F8] rounded-lg p-2.5 resize-none focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
          />
        </div>
      </ConfirmModal>
    </motion.div>
  );
}
